import { request } from 'umi';
import { match } from 'react-router-dom';
import { extend } from 'umi-request';
import { getAppSettings } from '@/shared/getAppSettings';

export interface GetInitialPropsFromServerContext<
  Params extends {
    [K in keyof Params]?: string;
  } = {},
> {
  // 服务端的 request 方法
  httpClient: typeof request;

  // 用户是否登录
  isUserLoggedIn?: boolean;

  // 当前语言
  language?: 'zh-CN' | 'en-US';

  // 路由匹配参数
  match: match<Params>;

  // 访问路径
  pathname: string;

  // 搜索字符串
  search: string;
}

export const getInitialPropsFromServer = <
  T = any,
  Params extends {
    [K in keyof Params]?: string;
  } = {},
>(
  component: any,
  initializer: (
    serverContext: GetInitialPropsFromServerContext<Params>,
  ) => Promise<T>,
) => {
  if (component) {
    component.getInitialProps = async (params: {
      isServer: boolean;
      serverContext: GetInitialPropsFromServerContext<Params>;
      match: match<Params>;
      history: {
        location: {
          search: string;
          query: any;
        };
      };
    }) => {
      if (params.isServer) {
        let context: GetInitialPropsFromServerContext<Params> | undefined;

        if (params.serverContext) {
          context = params.serverContext;
          context.match = params.match;
          context.search = params.history.location.search;
          context.pathname = params.match.url;
        } else if (process.env.NODE_ENV === 'development') {
          // 开发环境 mock httpClient
          const httpClient: typeof request = (url, options) => {
            const isAbsoluteUrl = (val: string) => {
              return (
                typeof val === 'string' && /^([a-z][0-9a-z]+\:)?\/\//i.test(val)
              );
            };

            if (isAbsoluteUrl(url)) {
              throw new Error(
                `SSR.httpClient: Url cannot be absolute. ${url} is invalid.`,
              );
            }

            let { apiHost } = getAppSettings();

            // mock 在 node 环境中需要补全协议
            if (apiHost.startsWith('//')) {
              apiHost = `http:${apiHost}`;
            }

            return extend({ prefix: apiHost })(url, options);
          };

          context = {
            httpClient,
            isUserLoggedIn: false,
            match: params.match,
            search: params.history.location.search,
            pathname: params.match.url,
          };
        }

        if (context) {
          const client = context.httpClient;
          context.httpClient = async (url, options) => {
            return client(url, options).then((res) => {
              if (!options?.skipErrorHandler) {
                const data = options?.getResponse ? res.data : res;
                if (data) {
                  if (typeof data.code === 'number' && data.code !== 0) {
                    return Promise.reject({
                      ...data,
                      success: false,
                      errorMessage: data.msg,
                      errorCode: data.errorCode || `${data.code}`,
                    });
                  } else if (data.success === false) {
                    return Promise.reject({
                      ...data,
                      errorMessage: data.errorMessage || data.errorMsg,
                    });
                  }
                }
              }
              return res;
            });
          };

          return initializer(context);
        }
      }

      return undefined;
    };
  }
};
